import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { isUndefined, map } from 'lodash';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Spinner,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from 'reactstrap';
import { PayerErrorPopover } from '../Error/ErrorPopover';
import { EditIconButton } from '../Button/EditIconButton';
import { SaveIconButton } from '../Button/SaveIconButton';
import { CancelIconButton } from '../Button/CancelIconButton';

const DivWithHiddenEditButton = ({ children, value, setEditing, textWidth = '100%', tooltip = false }) => {
  const [mouseIsOver, setMouseIsOver] = useState(false);

  const editButton = (
    <EditIconButton
      style={{ ...(!mouseIsOver && { visibility: 'hidden' }) }}
      onClick={() => {
        setEditing(true);
        setMouseIsOver(false);
      }}
    />
  );
  const ref = tooltip ? React.createRef() : undefined;
  return (
    <div
      style={{ display: 'flex', flexDirection: 'row' }}
      onMouseEnter={() => setMouseIsOver(true)}
      onMouseLeave={() => setMouseIsOver(false)}
    >
      <span
        ref={ref}
        style={{
          overflow: 'hidden',
          paddingRight: '5px',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: textWidth,
        }}
      >
        {!isUndefined(children) ? children : value}
      </span>
      {tooltip && (
        <UncontrolledTooltip target={ref} placement="bottom" isOpen={mouseIsOver}>
          {tooltip}
        </UncontrolledTooltip>
      )}
      {editButton}
    </div>
  );
};

DivWithHiddenEditButton.propTypes = {
  children: PropTypes.node,
  value: PropTypes.node,
  setEditing: PropTypes.func.isRequired,
  textWidth: PropTypes.string,
  tooltip: PropTypes.node,
};

const Editable = ({
  value,
  setValue,
  isFetching,
  error,
  resetError = () => {},
  EditComponent,
  ViewComponent = DivWithHiddenEditButton,
}) => {
  const ref = React.createRef();
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!editing && !isFetching) {
      setDraft(value);
    }
  }, [value, editing, isFetching]);

  const saveButton = (
    <SaveIconButton
      onClick={() => {
        setValue(draft);
        setEditing(false);
      }}
      disabled={isFetching || draft === value}
    />
  );

  const cancelButton = (
    <CancelIconButton
      onClick={() => {
        setEditing(false);
      }}
      disabled={isFetching}
    />
  );

  return (
    <>
      <div ref={ref}>
        {editing || (isFetching && value !== draft) ? (
          <EditComponent
            draft={draft}
            setDraft={setDraft}
            saveButton={saveButton}
            cancelButton={cancelButton}
            isFetching={isFetching}
            setEditing={setEditing}
          />
        ) : (
          <ViewComponent value={value} setEditing={setEditing} />
        )}
      </div>
      <PayerErrorPopover target={ref} error={error} onClose={resetError} />
    </>
  );
};

Editable.propTypes = {
  value: PropTypes.any,
  setValue: PropTypes.func.isRequired,
  isFetching: PropTypes.bool,
  error: PropTypes.shape({
    code: PropTypes.string,
    message: PropTypes.string,
    stack: PropTypes.string,
  }),
  resetError: PropTypes.func,
  EditComponent: PropTypes.elementType.isRequired,
  ViewComponent: PropTypes.elementType.isRequired,
};

const DropdownEditable = ({
  items,
  value,
  setValue,
  isFetching,
  error,
  resetError,
  ViewComponent = ({ value, setEditing }) => (
    <DivWithHiddenEditButton setEditing={setEditing}>{items[value]}</DivWithHiddenEditButton>
  ),
}) => (
  <Editable
    value={value}
    setValue={setValue}
    isFetching={isFetching}
    error={error}
    resetError={resetError}
    EditComponent={({ draft, setDraft, saveButton, cancelButton, isFetching }) => (
      <InputGroup>
        <InputGroupAddon addonType="append">
          <UncontrolledDropdown disabled={isFetching}>
            <DropdownToggle caret outline>
              {items[draft]}
            </DropdownToggle>
            <DropdownMenu>
              {map(items, (item, key) => (
                <DropdownItem key={key} onClick={() => setDraft(key)}>
                  {item}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
          {isFetching ? (
            <InputGroupText>
              <Spinner size="sm" />
            </InputGroupText>
          ) : (
            <>
              {saveButton}
              {cancelButton}
            </>
          )}
        </InputGroupAddon>
      </InputGroup>
    )}
    ViewComponent={ViewComponent}
  />
);

DropdownEditable.propTypes = {
  items: PropTypes.object.isRequired,
  value: PropTypes.any,
  setValue: PropTypes.func.isRequired,
  isFetching: PropTypes.bool,
  error: PropTypes.shape({
    code: PropTypes.string,
    message: PropTypes.string,
    stack: PropTypes.string,
  }),
  resetError: PropTypes.func,
  ViewComponent: PropTypes.elementType.isRequired,
};

const InputEditable = ({
  type = 'text',
  value = '',
  setValue,
  isFetching,
  error,
  resetError,
  ViewComponent = DivWithHiddenEditButton,
  maxLength = 1024,
}) => (
  <Editable
    value={value}
    setValue={setValue}
    isFetching={isFetching}
    error={error}
    resetError={resetError}
    EditComponent={({ draft, setDraft, saveButton, cancelButton, isFetching, setEditing }) => (
      <InputGroup>
        <Input
          type={type}
          value={draft}
          onChange={event => {
            setDraft(event.target.value);
          }}
          onKeyDown={event => event.stopPropagation()}
          onBlur={() => {
            if (draft === value) {
              setEditing(false);
            }
          }}
          disabled={isFetching}
          autoFocus
          maxLength={maxLength}
        />
        <InputGroupAddon addonType="append">
          {isFetching ? (
            <InputGroupText>
              <Spinner size="sm" />
            </InputGroupText>
          ) : (
            <>
              {saveButton}
              {cancelButton}
            </>
          )}
        </InputGroupAddon>
      </InputGroup>
    )}
    ViewComponent={ViewComponent}
  />
);

InputEditable.propTypes = {
  type: PropTypes.string,
  value: PropTypes.any,
  setValue: PropTypes.func.isRequired,
  isFetching: PropTypes.bool,
  error: PropTypes.shape({
    code: PropTypes.string,
    message: PropTypes.string,
    stack: PropTypes.string,
  }),
  resetError: PropTypes.func,
  ViewComponent: PropTypes.elementType.isRequired,
  maxLength: PropTypes.number,
};

export { Editable, DropdownEditable, InputEditable, DivWithHiddenEditButton };
